package com.islamicmedia.shared.util

/**
 * Invisible characters a soft keyboard can leave in a text field.
 *
 * `trim()` does NOT remove any of these. Kotlin trims on `Char.isWhitespace()`, which is false for
 * every mark below — including the non-breaking spaces. So an address or code that looks perfect on
 * screen arrives at the server malformed, and the user has no way to see why.
 *
 * This is not hypothetical tidying: an ARABIC keyboard inserts bidi marks around Latin text, which
 * is what made signup fail with GoTrue's "Unable to validate email address: invalid format" on an
 * address that worked fine when typed on a Latin keyboard. The same input broke voucher redemption.
 */
private val INVISIBLE_INPUT_MARKS = setOf(
    // Bidirectional controls — inserted by RTL keyboards (Arabic, Hebrew, Farsi, Urdu) around
    // embedded Latin runs like an email address or a promo code.
    '‎', // LEFT-TO-RIGHT MARK
    '‏', // RIGHT-TO-LEFT MARK
    '‪', // LEFT-TO-RIGHT EMBEDDING
    '‫', // RIGHT-TO-LEFT EMBEDDING
    '‬', // POP DIRECTIONAL FORMATTING
    '‭', // LEFT-TO-RIGHT OVERRIDE
    '‮', // RIGHT-TO-LEFT OVERRIDE
    '⁦', // LEFT-TO-RIGHT ISOLATE
    '⁧', // RIGHT-TO-LEFT ISOLATE
    '⁨', // FIRST STRONG ISOLATE
    '⁩', // POP DIRECTIONAL ISOLATE
    // Zero-width joiners/spaces — autocorrect and emoji keyboards emit these.
    '​', // ZERO WIDTH SPACE
    '‌', // ZERO WIDTH NON-JOINER
    '‍', // ZERO WIDTH JOINER
    '﻿', // ZERO WIDTH NO-BREAK SPACE / BOM
    // Non-breaking spaces. isWhitespace() is FALSE for all three, so trim() leaves them behind.
    ' ', // NO-BREAK SPACE
    ' ', // FIGURE SPACE
    ' ', // NARROW NO-BREAK SPACE
)

/**
 * Strip invisible marks anywhere in the string, then trim ordinary whitespace.
 *
 * Removes them THROUGHOUT rather than only at the ends: an RTL keyboard can plant a mark between
 * the local part and the domain, or mid-code, not just at the edges.
 *
 * Safe for the values it's used on — email addresses and promo codes can't legitimately contain any
 * of these. Don't reach for it on free text (a comment, a name) where a joiner may be meaningful.
 */
fun String.sanitizeTypedInput(): String =
    filterNot { it in INVISIBLE_INPUT_MARKS }.trim()
